import {
  ViewStyle,
  View,
  StyleSheet,
  Pressable,
  PressableProps,
  StyleProp,
  TextStyle,
  PressableStateCallbackType,
} from "react-native";
import { LegacyRef, forwardRef } from "react";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import Color from "color";

export type ButtonProps = PressableProps & {
  ref?: LegacyRef<View>;
  text?: string;
  enabled?: boolean;
  onPress?: () => any;
  iconPosition?: "left" | "right";
  icon?: keyof typeof MaterialCommunityIcons.glyphMap;
  hoverColor?: ViewStyle["backgroundColor"];
  iconSize?: number;
  /**
   * @deprecated Use `iconStyle` instead. This prop will be removed in a future version.
   */
  iconColor?: string;
  iconStyle?: { color?: string } | ((state: PressableStateCallbackType) => { color?: string });
  style?: StyleProp<ViewStyle> | ((state: PressableStateCallbackType) => StyleProp<ViewStyle>);
  toolTip?: string;
  textStyle?: StyleProp<TextStyle> | ((state: PressableStateCallbackType) => StyleProp<TextStyle>);
};

export const Button = forwardRef<View, ButtonProps>(function ButtonBase(
  {
    hoverColor: hc,
    textStyle,
    enabled = true,
    text,
    onPress,
    icon,
    iconSize = 20,
    iconColor,
    iconStyle,
    iconPosition = "left",
    style,
    ...rest
  },
  ref,
) {
  const { colors } = useTheme();

  // Safely extract backgroundColor from style prop, handling both objects and arrays
  const getBackgroundColor = (): string | undefined => {
    if (!style) return undefined;

    if (typeof style === "function") {
      return undefined;
    }

    const flattenedStyle = StyleSheet.flatten(style as StyleProp<ViewStyle>) as ViewStyle;
    return flattenedStyle && flattenedStyle.backgroundColor ? String(flattenedStyle.backgroundColor) : undefined;
  };

  const backgroundColor = getBackgroundColor();
  const isTransparent = backgroundColor === "transparent";

  const hoverColor = hc
    ? hc
    : isTransparent
      ? colors.border
      : colors.name === "dark"
        ? Color(backgroundColor).mix(Color(colors.text), 0.2).toString()
        : Color(backgroundColor).mix(Color(colors.border), 0.6).toString();

  const pressedColor =
    colors.name === "dark"
      ? Color(backgroundColor).mix(Color(hoverColor), 0.5).toString()
      : Color(backgroundColor).mix(Color(hoverColor), 0.5).toString();

  return (
    <Pressable
      ref={ref}
      {...rest}
      style={(state) => [
        { borderColor: colors.border, backgroundColor: colors.background },
        styles.buttonContainer,
        !enabled && { opacity: 0.5, pointerEvents: "none" },
        typeof style === "function" ? style(state) : style,
        state.hovered && enabled && { backgroundColor: hoverColor },
        state.pressed && enabled && { backgroundColor: pressedColor },
      ]}
      onPress={enabled ? onPress : () => {}}
    >
      {(state) => {
        // Determine icon color: iconStyle > iconColor > default
        const getIconColor = () => {
          if (iconStyle) {
            const style = typeof iconStyle === "function" ? iconStyle(state) : iconStyle;
            return style?.color || colors.buttonText;
          }
          return iconColor || colors.buttonText;
        };

        const currentIconColor = getIconColor();

        return (
          <>
            {icon && iconPosition === "left" && (
              <MaterialCommunityIcons name={icon} size={iconSize} color={currentIconColor} />
            )}
            {text && (
              <ThemedText
                style={[
                  { textAlign: "center", color: colors.buttonText },
                  typeof textStyle === "function" ? textStyle(state) : textStyle,
                ]}
              >{`${text.toString()}`}</ThemedText>
            )}
            {icon && iconPosition === "right" && (
              <MaterialCommunityIcons name={icon} size={iconSize} color={currentIconColor} />
            )}
          </>
        );
      }}
    </Pressable>
  );
});

const styles = StyleSheet.create({
  buttonContainer: {
    flexDirection: "row",
    borderWidth: StyleSheet.hairlineWidth,
    minWidth: 28,
    borderRadius: 6,
    minHeight: 28,
    alignItems: "center",
    outlineColor: "transparent",
    paddingHorizontal: 3,
    justifyContent: "space-evenly",
  },
});
